import React, { useState, useEffect, use } from 'react';
import axios from 'axios';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBookingDetails } from '@/redux/Booking/actions';
import { getAddressAPI } from '@/redux/Purchase/actions';
import { BookingPrintHTML } from '@/pages/Mail/Layout/BookingMail';
import { RateQuoteMail } from './Layout/RateQuoteMail';
import { pdfblobURL } from './PDFCreate';
import { getRateQuoteByID } from '@/redux/RateQuote/actions';
import { getImportRateQuoteByID } from '@/redux/ImportRateQuote/actions';
import { getClauselist, SendEmail } from '@/redux/Common/actions';
import { loaderStart, loaderStop } from '@/redux/Loader/actions';
import { Switch, SwitchThumb } from '@radix-ui/react-switch';
import { set } from 'date-fns';
import { RateQuotePDF } from '@/components/PDF/RateQuotePDF';

const ComposeMail = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const type = searchParams.get('type');
    const [emailData, setEmailData] = useState(null);
    const [signature, setSignature] = useState('');
    const [clauses, setClauses] = useState();

    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList, commodityGroupList, containerTypeList, operatorList, moveTypeList, MasterCompanyDetails } = useSelector((state) => state.common);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");


    const [formData, setFormData] = useState({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        HasAttachment: true
    });
    const [bookingCustomerAddress, setBookingCustomerAddress] = useState('');
    const [sending, setSending] = useState(false);
    const [autoSend, setAutoSend] = useState(false);

    useEffect(() => {
        if (id && type) {
            _getFormData();

            setSignature(`
                <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size:14px; color:#000; margin-top:20px; margin-bottom:20px;">
                    <tr>
                        <td style="padding-right:15px; vertical-align:middle;">
                            <img src="https://infox.com/img/infox_logo.png" alt="Infox Logo" width="120" style="display:block; border:0;" />
                        </td>
                        <td style="border-left:2px solid #ccc; padding-left:15px;">
                            <p style="margin:0; font-size:16px; font-weight:bold; color:#c40000;">
                               ${authUser?.firstName} ${authUser?.lastName}
                            </p>
                            <p style="margin:2px 0 8px 0; font-size:13px; color:#555;">
                                Support Team
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2" style="padding-top:10px; border-top:1px solid #eee;">
                            <div style="display:flex; flex-direction:row; margin-top:10px;">
                                <p style= display:flex; align-items:center; font-size:13px; color:#555;">
                                    <img src="https://cdn-icons-png.flaticon.com/16/561/561127.png" width="14" style="vertical-align:middle; width:18px; height:18px;" />
                                    <a href="mailto:ashish@infox.com" style="color:#1a73e8; text-decoration:none; margin-left:5px;">
                                    ashish@infox.com
                                    </a>
                                </p> 
                                <p style=" display:flex; align-items:center; font-size:13px; color:#555; margin-left:20px;">
                                    <img src="https://cdn-icons-png.flaticon.com/16/535/535239.png" width="14" style="vertical-align:middle; width:18px; height:18px;" />
                                    <a href="https://www.infox.com" style="color:#1a73e8; text-decoration:none; margin-left:5px;">
                                        www.infox.com
                                    </a>
                                </p>
                            </div>
                        </td>
                    </tr>
                </table>`
            );
        }
    }, [id, type]);

    const mergeAllInCharges = (data) => {
        if (!data?.CarrierDetails) return data;
        const updatedCarriers = data.CarrierDetails.map((carrier) => {
            const charges = carrier?.rateCharges || [];
            const allInCharges = charges.filter(c => c.all_in);
            const normalCharges = charges.filter(c => !c.all_in);
            if (allInCharges.length === 0) return carrier;
            const grouped = allInCharges.reduce((acc, item) => {
                const key = item.basis || "UNKNOWN";
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
            }, {});
            const keys = [
                "set1sellrate", "set1buyrate",
                "set2sellrate", "set2buyrate",
                "set3sellrate", "set3buyrate"
            ];
            const mergedCharges = Object.values(grouped).map(group => {
                const base = group[0];
                const merged = {
                    ...base,
                    chargedescription: `Ocean Freight (All-In)`,
                };
                keys.forEach(key => {
                    const total = group.reduce((sum, item) => sum + (item[key] || 0), 0);
                    merged[key] = total; // or total if you don't want average
                });
                return merged;
            });
            return {
                ...carrier,
                rateCharges: [...normalCharges, ...mergedCharges],
            };
        });
        return {
            ...data,
            CarrierDetails: updatedCarriers,
        };
    };

    const _getFormData = async () => {
        try {
            switch (type) {
                case 'RateQuote': {
                    const resultData = await dispatch(getRateQuoteByID(id));
                    const processedData = mergeAllInCharges(resultData);
                    setEmailData(processedData);
                    _getClauselistData(processedData?.rateQuoteClauses || []);
                    setFormData(prev => ({ ...prev, subject: `RateQuote Invoice ${processedData?.rateQuoteNumber}` }));
                    break;
                }
                case 'ImportRateQuote': {
                    const resultData = await dispatch(getImportRateQuoteByID(id));
                    setEmailData(resultData);
                    _getClauselistData(resultData?.rateQuoteClauses || []);
                    setFormData(prev => ({ ...prev, subject: `Import RateQuote Invoice ${resultData?.rateQuoteNumber}` }));
                    break;
                }

                // ✅ NEW CASE
                case 'Booking': {
                    const resultData = await dispatch(getBookingDetails(id,authUser?.companyId));
                    setEmailData(resultData);

                    // Pre-fill subject and recipient email if available
                    setFormData(prev => ({
                        ...prev,
                        subject: `Booking Confirmation - ${resultData?.fileNumber || id}`,
                        to: resultData?.email || prev.to,  // auto-fill customer email
                    }));

                    // Fetch customer address (same as BookingPrint does)
                    if (resultData?.customerId) {
                        const address = await getAddressAPI(resultData.customerId);
                        setBookingCustomerAddress(address || '');
                    }
                    break;
                }
            }
        } catch (error) {
            console.error('Error fetching data for email:', error);
            toast.error('Failed to load email data');
        }
    };

    const _getClauselistData = async (formClauses = []) => {
        try {
            const payload = {
                IPageNo: 1,
                IPageRecords: 1000,
                Type: 3,
            };
            const clauseData = await dispatch(getClauselist(payload));

            const transformedData = formClauses.map((item) => {
                const matchedClause = clauseData?.find(
                    (c) => c.ClauseTypeDescriptionID === item.clauseTypeDescID,
                );

                return {
                    ClauseTypeDescriptionID: matchedClause?.ClauseTypeDescriptionID,
                    ClauseType: matchedClause?.ClauseType,
                    ClauseDescription: matchedClause.ClauseDescription,
                    ClausesCategoryType: matchedClause?.ClausesCategoryType,
                    ModuleType: matchedClause?.ModuleType,
                };
            });

            setClauses(transformedData);
        } catch (error) {
            console.error("Error loading clauses:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.to || !formData.subject || !formData.body) {
            toast.error('Please fill in all required fields');
            return;
        }

        await dispatch(loaderStart());

        var html = `<p style="margin-bottom:30px;">${formData.body}</p>`;
        var pdfHtml = '';

        switch (type) {
            case 'RateQuote': {
                html += await RateQuoteMail({ formData: emailData, salesPersonList, clauses });
                pdfHtml += await RateQuotePDF({ formData: emailData, salesPersonList, clauses });
                break;
            }
            case 'ImportRateQuote': {
                html += await RateQuoteMail({ formData: emailData, salesPersonList, clauses });
                pdfHtml += await RateQuotePDF({ formData: emailData, salesPersonList, clauses });
                break;
            }

            // ✅ NEW CASE
            case 'Booking': {
                html += `<p style="margin-bottom:20px;">Please find your booking confirmation attached.</p>`;

                pdfHtml = BookingPrintHTML({
                    formData: emailData,
                    customerAddress: bookingCustomerAddress,
                    MasterCompanyDetails,  // already in scope from useSelector
                });
                break;
            }
        }

        const payload = new FormData();
        payload.append('CompanyId', authUser?.companyId || 0);
        payload.append('FromEmail', authUser?.emailID);
        payload.append('ToEmail', formData?.to);
        payload.append('Cc', formData?.cc);
        payload.append('Bcc', formData?.bcc);
        payload.append('Subject', formData.subject);
        if (formData.HasAttachment === true) {
            const { pdfBlob, pdfUrl } = await pdfblobURL(pdfHtml, `${type}_${id}.pdf`);
            payload.append('FileData', pdfBlob, `${type}_${id}.pdf`);
        }
        payload.append('Body', `${html} ${signature}`);
        payload.append('FileDirectory', type);
        payload.append('HasAttachment', formData.HasAttachment);

        try {
            await SendEmail(payload).then(result => {
                console.log('Email API Result:', result);
                toast.success('Email sent successfully');
                setFormData({
                    to: '',
                    cc: '',
                    bcc: '',
                    subject: '',
                    body: '',
                    HasAttachment: true
                });
            }).catch(error => {
                console.error('Email API Error:', error);
                toast.error('Failed to send email');
            });
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email');
        } finally {
            await dispatch(loaderStop());
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm"
        >
            {/* Header */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-[#0072FF] transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span>Back</span>
                        </button>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Compose Email</h2>
                    </div>
                    <button
                        id="voice-send-button"
                        onClick={handleSubmit}
                        disabled={sending}
                        className="px-4 py-2 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                <span>Send</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* To */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        To <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        name="to"
                        value={formData.to}
                        onChange={handleChange}
                        placeholder="recipient@example.com"
                        required
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                </div>

                {/* CC & BCC */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            CC
                        </label>
                        <input
                            type="text"
                            name="cc"
                            value={formData.cc}
                            onChange={handleChange}
                            placeholder="cc@example.com"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            BCC
                        </label>
                        <input
                            type="text"
                            name="bcc"
                            value={formData.bcc}
                            onChange={handleChange}
                            placeholder="bcc@example.com"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                </div>

                {/* Subject */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Subject <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Email subject"
                            required
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Has attachment? <span className="text-red-500">*</span>
                        </label>
                        <Switch
                            className="relative h-[25px] w-[42px] cursor-default rounded-full bg-slate-300 dark:bg-slate-700 data-[state=checked]:bg-[#0072FF] dark:data-[state=checked]:bg-[#0072FF] focus:outline-none focus:ring-2 focus:ring-[#0072FF] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            id="airplane-mode"
                            style={{ "-webkit-tap-highlight-color": "rgba(0, 0, 0, 0)" }}
                            checked={formData.HasAttachment}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, HasAttachment: checked }))}
                        >
                            <SwitchThumb className="block size-[21px] translate-x-0.5 rounded-full bg-white shadow-[0_2px_2px] shadow-blackA4 transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[19px]" />
                        </Switch>
                    </div>
                </div>

                {/* Body */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="body"
                        value={formData.body}
                        onChange={handleChange}
                        placeholder="Type your message here..."
                        rows="12"
                        required
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Signature <span className="text-red-500">*</span>
                    </label>
                    <div className='bg-white py-10 px-10 border border-slate-300 dark:border-slate-700 rounded-lg' style={{ minHeight: '150px' }}>
                        <div
                            dangerouslySetInnerHTML={{ __html: signature }}
                        />
                    </div>
                </div>
            </form>
        </motion.div>
    );
};

export default ComposeMail;